import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, Scale, Clock } from 'lucide-react';
export const ForensicEvidenceSection = () => {
  return <section className="py-24 bg-[#050505] relative overflow-hidden border-y border-gray-900" data-id="element-2477">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-30 pointer-events-none" data-id="element-2478" />

      <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-2479">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-2480">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} data-id="element-2481">
            <div className="inline-flex items-center gap-2 mb-6 bg-[#FF0000]/5 border border-[#FF0000]/20 px-3 py-1 rounded-full" data-id="element-2482">
              <span className="w-1.5 h-1.5 bg-[#FF0000] rounded-full animate-pulse" data-id="element-2483" />
              <span className="text-[#FF0000] font-mono text-[10px] tracking-widest uppercase font-bold" data-id="element-2484">
                Legal-Grade Output
              </span>
            </div>

            <h2 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight leading-none" data-id="element-2485">
              Court-Ready <br data-id="element-2486" />
              <span className="text-[#FF0000]" data-id="element-2487">Documentation</span>
            </h2>

            <p className="text-gray-400 text-lg mb-8 leading-relaxed font-mono" data-id="element-2488">
              Don't just feel it—prove it. Generate comprehensive PDF dossiers
              that document patterns of abuse with timestamps, severity scores,
              and specific examples.
            </p>

            <div className="space-y-6" data-id="element-2489">
              <FeatureRow icon={<Scale size={20} data-id="element-2491" />} title="Objective Analysis" description="Removes emotional bias to present facts clearly." data-id="element-2490" />
              <FeatureRow icon={<Clock size={20} data-id="element-2493" />} title="Timeline Reconstruction" description="Maps escalation patterns over days, weeks, or months." data-id="element-2492" />
              <FeatureRow icon={<FileText size={20} data-id="element-2495" />} title="Pattern Classification" description="Identifies specific tactics like gaslighting, triangulation, and projection." data-id="element-2494" />
              <FeatureRow icon={<Shield size={20} data-id="element-2497" />} title="Secure Storage" description="Evidence is encrypted and stored securely in your private vault." data-id="element-2496" />
            </div>
          </motion.div>

          {/* Image Content */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-2498">
            <div className="absolute -inset-4 bg-[#FF0000]/5 rounded-2xl blur-2xl" data-id="element-2499" />

            <div className="relative rounded-xl overflow-hidden border border-[#FF0000]/30 shadow-[0_0_40px_rgba(255,0,0,0.15)] bg-black/50 backdrop-blur-sm group" data-id="element-2500">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-50" data-id="element-2501" />

              <img src="https://cdn.magicpatterns.com/uploads/tvdUram2Ph8avebcdq6zLS/file_00000000a198720c9cae59923f9b0abf_(1).png" alt="Forensic Dossier Evidence" className="w-full h-auto object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2502" />

              {/* Overlay UI */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-6 pt-12" data-id="element-2503">
                <div className="flex justify-between items-end border-t border-gray-800 pt-4" data-id="element-2504">
                  <div data-id="element-2505">
                    <div className="text-[#FF0000] font-mono text-[10px] tracking-[0.2em] font-bold uppercase mb-1" data-id="element-2506">
                      Evidence Package
                    </div>
                    <div className="text-white font-bold text-sm uppercase tracking-wide" data-id="element-2507">
                      Case File #992-AX
                    </div>
                  </div>
                  <div className="text-right" data-id="element-2508">
                    <div className="text-gray-500 font-mono text-[10px] uppercase tracking-wider mb-1" data-id="element-2509">
                      Status
                    </div>
                    <div className="text-[#FFD700] font-mono text-xs font-bold uppercase tracking-wide flex items-center gap-2 justify-end" data-id="element-2510">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FFD700] animate-pulse" data-id="element-2511" />
                      Verified
                    </div>
                  </div>
                </div>
              </div>

              {/* Corner Markers */}
              <div className="absolute top-4 left-4 w-2 h-2 border-t border-l border-[#FF0000]/50" data-id="element-2512" />
              <div className="absolute top-4 right-4 w-2 h-2 border-t border-r border-[#FF0000]/50" data-id="element-2513" />
              <div className="absolute bottom-4 left-4 w-2 h-2 border-b border-l border-[#FF0000]/50" data-id="element-2514" />
              <div className="absolute bottom-4 right-4 w-2 h-2 border-b border-r border-[#FF0000]/50" data-id="element-2515" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};
const FeatureRow = ({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => <div className="flex gap-4 group" data-id="element-2516">
    <div className="flex-shrink-0 w-10 h-10 rounded bg-[#FF0000]/10 border border-[#FF0000]/20 flex items-center justify-center text-[#FF0000] group-hover:bg-[#FF0000] group-hover:text-white transition-colors duration-300" data-id="element-2517">
      {icon}
    </div>
    <div data-id="element-2518">
      <h3 className="text-white font-bold uppercase tracking-wide text-sm mb-1 group-hover:text-[#FF0000] transition-colors" data-id="element-2519">
        {title}
      </h3>
      <p className="text-gray-500 text-sm font-mono leading-relaxed" data-id="element-2520">
        {description}
      </p>
    </div>
  </div>;